<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import type { AdminUserDetailsDto } from '@/types'

import { appConfirm } from '@/composables/useAppConfirm'
import { appTableSortableHeader } from '@/composables/useAppTable'
import { useAppToast } from '@/composables/useAppToast'
import { useDelayedLoading } from '@/composables/useDelayedLoading'
import { adminService } from '@/services/adminService'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

// Get the user ID from the route
const route = useRoute()
const router = useRouter()
const id = (route.params as { id: string }).id

// Toast for notifications
const toast = useAppToast()

// User data
const user = ref<AdminUserDetailsDto | null>(null)
const loading = ref<boolean>(true)
const showLoading = useDelayedLoading(loading)
const deleting = ref<boolean>(false)
type UserClaim = AdminUserDetailsDto['claims'][number]
const claimColumns: TableColumn<UserClaim>[] = [
  { accessorKey: 'type', header: appTableSortableHeader<UserClaim>('Claim Type') },
  { accessorKey: 'value', header: appTableSortableHeader<UserClaim>('Claim Value') },
]

// Fetch user details
const fetchUserDetails = async () => {
  loading.value = true
  try {
    user.value = await adminService.getUserById(id)
  } catch (error) {
    console.error('Error fetching user details:', error)
    toast.add({
      color: 'error',
      title: 'Error',
      description: 'Failed to load user details',
      duration: 5000,
    })
  } finally {
    loading.value = false
  }
}

const confirmDeleteUser = async () => {
  if (!user.value || deleting.value) {
    return
  }

  const userLabel = user.value.userName || user.value.email || user.value.id
  const accepted = await appConfirm({
    title: 'Delete User',
    message: `Permanently delete user "${userLabel}"? This also removes related personal rules and clears linked activity references.`,
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
    confirmColor: 'error',
  })
  if (!accepted) return

  deleting.value = true
  try {
    await adminService.deleteUser(id)
    toast.add({
      color: 'success',
      title: 'Success',
      description: 'User deleted successfully',
      duration: 3000,
    })
    await router.push('/admin/users')
  } catch (error: any) {
    console.error('Error deleting user:', error)
    const detail = error?.response?.data || 'Failed to delete user'
    toast.add({
      color: 'error',
      title: 'Error',
      description: typeof detail === 'string' ? detail : 'Failed to delete user',
      duration: 5000,
    })
  } finally {
    deleting.value = false
  }
}

// i18n
const { d } = useI18n()

onMounted(() => {
  fetchUserDetails()
})
</script>

<template>
  <div class="p-4">
    <div v-if="showLoading" class="flex items-center justify-center p-8">
      <AppSpinner style="width: 50px; height: 50px" />
    </div>

    <div v-else-if="user" class="grid grid-cols-1 gap-6 md:grid-cols-12">
      <!-- User info card -->
      <UCard class="md:col-span-8">
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-user" class="text-xl" />
            <span>User Details</span>
          </div>
        </template>

        <template #default>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Username</h3>
              <p class="mt-1">
                {{ user.userName || 'N/A' }}
              </p>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Email</h3>
              <p class="mt-1">
                {{ user.email || 'N/A' }}
              </p>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Full Name</h3>
              <p class="mt-1">{{ user.firstName || '' }} {{ user.lastName || '' }}</p>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Last Login</h3>
              <p class="mt-1">
                {{ user.lastLogin ? d(new Date(user.lastLogin), 'datetimefull') : 'N/A' }}
              </p>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</h3>
              <p class="mt-1">
                {{ user.createdAt ? d(new Date(user.createdAt), 'datetimefull') : 'N/A' }}
              </p>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">Roles</h3>
              <p class="mt-1">
                {{ user.roles?.join(', ') || 'None' }}
              </p>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400">User Type</h3>
              <p class="mt-1">
                {{ user.userType || 'N/A' }}
              </p>
            </div>
          </div>
        </template>
      </UCard>

      <!-- User actions card -->
      <UCard class="md:col-span-4">
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-settings" class="text-xl" />
            <span>User Actions</span>
          </div>
        </template>

        <template #default>
          <UButton
            label="View Audit Log"
            icon="i-lucide-history"
            :to="{ path: '/admin/audit', query: { userId: user.id, userFilter: 'actor' } }"
            class="mb-2 w-full"
            variant="outline"
          />
          <UButton
            label="Delete User"
            icon="i-lucide-trash"
            color="error"
            :loading="deleting"
            class="w-full"
            @click="confirmDeleteUser"
            variant="outline"
          />
        </template>
      </UCard>

      <!-- Claims card -->
      <UCard class="md:col-span-12">
        <template #header>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-key" class="text-xl" />
            <span>User Claims</span>
          </div>
        </template>

        <template #default>
          <AppDataTableShell :data="user.claims" :columns="claimColumns" paginate :page-size="5" />
        </template>
      </UCard>
    </div>

    <div v-else class="p-8 text-center">
      <UIcon
        name="i-lucide-triangle-alert"
        class="mb-4 text-4xl text-yellow-700 dark:text-yellow-400"
      />
      <h2 class="text-xl font-bold">User Not Found</h2>
      <p class="mt-2">The requested user could not be found.</p>
      <router-link to="/admin/users" class="mt-4 inline-block text-blue-700 dark:text-blue-400">
        Return to User List
      </router-link>
    </div>
  </div>
</template>
